
/**
 * This module is the mediator who control all the events in the app. 
 * This module is the bridge between all the modules. All modules communicate 
 * with each other using this module
 * @module mediator
 * @version 1
 * 
 */

import { Login } from 'dac-web-utils';
import { DataManager } from '../../js/managers/dataManager.js';
import { InitFormManager } from '../../js/managers/initFormManager.js';
import { GridManager } from '../../js/managers/gridManager.js';
import { SceneManager, SCENES } from '../../js/managers/sceneManager.js';
import mainHTML from '../../../main.module.html';
import { ToolBox2DManager } from '../../js/managers/toolBox2DManager.js';
import { ToolBox3DManager } from '../../js/managers/toolBox3DManager.js';
import { ToolBoxGroupManager } from '../../js/managers/toolBoxGroupManager.js';
import { ToolBoxZoneManager } from '../../js/managers/toolBoxZoneManager.js';

import { SizeSelector } from '../../js/sizeSelector/sizeSelector.js';
import { AquaManager } from '../../js/managers/aquaManager.js';
import { Loading } from '../loading/loading.js';
import { QuestionFormManager } from '../../js/managers/questionFormManager.js';
import { CellSelection } from '../cellSelection/cellSelection.js';
import { List } from '../list/list.js';
import { GROUP_TYPE } from '../../js/structures/aqua/gridAqua.js';
import { EDIT_MODE } from '../3d/sceneZone/sceneZoneUnit.js';

export class Mediator {

	constructor() {
		this.modules = {};
		this.managers = {};
		this.inputScreens = [];
		this.forms = [];

		this._loading = new Loading( 'loadingContainer' );
	}

	async init() {
		document.getElementById('splash').dataset.visible = false;

		//this.#login();

		//TODO: after adding login, add this function in the 'onLogin' callback
		await new DataManager( this.modules, this.managers ).init();

		await this.#onLogin();		
	}

	#login() {
		this.modules.login = new Login({ register: true, container: 'login' });

		this.modules.login.subscribe('onLogout', async ( /* logoutData */ ) => {
			/*await this.clear();
			document.querySelector('canvas').remove();
			this.hideShowElements('hide');
			document.querySelector( '.loginBack' ).style.display = 'none';
			this.modules.login.openLoginForm();*/
		});
		this.modules.login.subscribe('onLogin', async () => await this.#onLogin() );
		this.modules.login.openLoginForm();
	}

	async #onLogin() {

		//init initial form
		new InitFormManager(this.modules, this.managers);
		await this.managers.InitFormManager.init();

		const initFormAccept = async () => {

			//unsubscribe initFormManager
			this.managers.InitFormManager.unSubscribe( 'accept', initFormAccept );

			//change html
			document.body.innerHTML = mainHTML;
			
			this._loading.show();
			
			//init Aqua manager to parse grid to Aqua format
			new AquaManager(this.modules, this.managers);

			//init toolboxManagers
			new ToolBox2DManager(this.modules, this.managers);
			this.managers.ToolBox2DManager.subscribe( 'onAddRow' , this.#onAddRow.bind( this ) );
			this.managers.ToolBox2DManager.subscribe( 'onAddColumn' , this.#onAddColumn.bind( this ) );
			this.managers.ToolBox2DManager.subscribe( 'onDeleteCells' , this.#onDeleteCells.bind( this ) );
			this.managers.ToolBox2DManager.subscribe( 'from2DtoInit' , this.#from2DtoInit.bind( this ) );
			this.managers.ToolBox2DManager.subscribe( 'from2Dto3D' , this.#from2Dto3D.bind( this ) );
			this.managers.ToolBox2DManager.subscribe( 'onSelectButtonPushed' , this.#onSelectButtonPushed.bind( this ) );

			new ToolBox3DManager(this.modules, this.managers);
			this.managers.ToolBox3DManager.subscribe( 'backTo2D' , this.#from3Dto2D.bind( this ) );
			this.managers.ToolBox3DManager.subscribe( 'onGroupSelection' , this.#from3DToGroup.bind( this ) );
			this.managers.ToolBox3DManager.subscribe( 'onZoneSelection' , this.#from3DToZone.bind( this ) );
			
			new ToolBoxGroupManager( this.modules, this.managers );
			this.managers.ToolBoxGroupManager.subscribe( 'fromGroupto3D' , this.#from2Dto3D.bind( this ) );
			this.managers.ToolBoxGroupManager.subscribe( 'onShowGroup' , this.#onShowGroup.bind( this ) );
			new ToolBoxZoneManager( this.modules, this.managers );
			this.managers.ToolBoxZoneManager.subscribe( 'fromZoneto3D' , this.#from2Dto3D.bind( this ) );
			this.managers.ToolBoxZoneManager.subscribe( 'onCreateZones' , this.#onCreateZones.bind( this ) );
			this.managers.ToolBoxZoneManager.subscribe( 'onAddZone' , this.#onAddZone.bind( this ) );
			this.managers.ToolBoxZoneManager.subscribe( 'onDeleteZone' , this.#onDeleteZone.bind( this ) );
			
			//init grid manager
			new GridManager(this.modules, this.managers);
			//generate initial grid & cells
			const grid = await this.managers.GridManager.init( this.managers.DataManager.currentData );

			//pass grid to projectScene and start drawing
			new SceneManager(this.modules, this.managers);
			
			await this.managers.SceneManager.init( grid );
			this._loading.hide();

			this.managers.SceneManager.scene2DUnit.subscribe( 'onCellsSelected', this.#onCellsSelected.bind( this ) );
			this.managers.SceneManager.renderUnit.subscribe( 'onResize', this.#onThreeSceneChanged.bind( this ) );
			this.managers.SceneManager.scene2DUnit.subscribe( 'onRowLengthChange', this.#onRowLengthChange.bind( this ) );			
			this.managers.SceneManager.scene2DUnit.subscribe( 'onColumnWidthChange', this.#onColumnWidthChange.bind( this ) );
			this.managers.SceneManager.scene2DUnit.controls.addEventListener( 'change', this.#onThreeSceneChanged.bind( this ) );
			this.managers.SceneManager.sceneGroupUnit.subscribe( 'onObjectSelected', this.#onGroupSelected.bind( this ) );
			this.managers.SceneManager.sceneZoneUnit.subscribe( 'onCellsSelected', this.#onZoneCellsSelected.bind( this ) );
			this.managers.SceneManager.sceneZoneUnit.subscribe( 'onZoneSelected', this.#onZoneSelected.bind( this ) );
			
			this.managers.SceneManager.render();
			//start with orthographic scene
			this.sceneChanged( SCENES.ORTHO );
			new QuestionFormManager(this.modules, this.managers);
		};

		this.managers.InitFormManager.subscribe( 'accept', initFormAccept );

		this.managers.InitFormManager.subscribe( 'cancel', async () => {});
	}

	#onCellsSelected() {		
		const selectedCells = this.managers.GridManager.grid.getSelectedCells();
		this.managers.ToolBox2DManager.enableControl( 'deleteCells', selectedCells.length > 0 );
	}

	async #onAddRow( rowLength ) {
		this.managers.GridManager.grid.addRow( rowLength );
		await this.managers.SceneManager.scene2DUnit.initGrid();
		this.managers.SceneManager.render();
		this.managers.ToolBox2DManager.moveRowColumnButtons();
	}

	async #onAddColumn( columnWidth ) {
		this.managers.GridManager.grid.addColumn( columnWidth );
		await this.managers.SceneManager.scene2DUnit.initGrid();
		this.managers.SceneManager.render();
		this.managers.ToolBox2DManager.moveRowColumnButtons();
	}

	async #onDeleteCells(){
		const selectedCells = this.managers.GridManager.grid.getSelectedCells();
		
		// set selected cells not visible
		this.managers.GridManager.deleteCells( selectedCells );
		
		//disable seleced cells
		this.managers.SceneManager.scene2DUnit.selectCells( selectedCells, false );
		
		await this.managers.SceneManager.scene2DUnit.initGrid();
		this.managers.SceneManager.render();
	}

	async #onThreeSceneChanged( size ) {
		if( !this.managers.ToolBox2DManager ) return;

		if( size.width && size.height ) this.managers.SceneManager.resize( size );

		if( this.currentScene === SCENES.ORTHO ) this.managers.ToolBox2DManager.moveRowColumnButtons();
		else if( this.currentScene == SCENES.GROUP && size ) this.managers.SceneManager.sceneGroupUnit.moveCutDeleteButtons( size );
		else if( this.currentScene === SCENES.ZONE ) this.managers.ToolBoxZoneManager.moveSelectedZoneButtons();
	}

	async #onRowLengthChange( row ) {
		const sizes = this.managers.DataManager.getMallas();
		const selectedSize = await new SizeSelector().show( 'Seleccione malla', sizes, row.cells[0].length );
		if( !selectedSize )	return;

		//change to new size
		for( let i=0;i < row.cells.length; i++ ) {
			const cell = row.cells[ i ];
			cell.length = parseFloat( selectedSize );
		}

		//refresh grid
		await this.managers.SceneManager.scene2DUnit.initGrid();
		this.managers.SceneManager.render();
		this.managers.ToolBox2DManager.moveRowColumnButtons();
	}

	async #onColumnWidthChange( column ){
		const sizes = this.managers.DataManager.getAnchos();
		const selectedSize = await new SizeSelector().show( 'Seleccione Ancho', sizes, column.cells[0].length );
		if( !selectedSize )	return;

		//change to new size
		for( let i=0;i < column.cells.length; i++ ) {
			const cell = column.cells[ i ];
			cell.width = parseFloat( selectedSize );
		}

		//refresh grid
		await this.managers.SceneManager.scene2DUnit.initGrid();
		this.managers.SceneManager.render();
		this.managers.ToolBox2DManager.moveRowColumnButtons();

	}

	async #from2DtoInit() {
		
		await this.managers.InitFormManager.init( 'modalContainer');

		const _clearInitForm = () => {
			//clear container
			document.getElementById('modalContainer').innerHTML = '';
			//unsubscribe initFormManager
			this.managers.InitFormManager.unSubscribe( 'accept', initFormAccept );
			this.managers.InitFormManager.unSubscribe( 'cancel', _clearInitForm );			
		};

		const initFormAccept = async () => {

			//clear container
			_clearInitForm();
			
			this._loading.show();

			//generate initial grid & cells
			const grid = await this.managers.GridManager.init( this.managers.DataManager.currentData );

			//pass grid to scenes and start drawing
			this.managers.SceneManager.scene2DUnit.grid = grid;
			this.managers.SceneManager.scene3DUnit.grid = grid;

			await this.managers.SceneManager.scene2DUnit.initGrid();
			this.managers.SceneManager.render();

			this._loading.hide();
			
			//start with orthographic scene
			this.sceneChanged( SCENES.ORTHO );

			new QuestionFormManager(this.modules, this.managers);
		};
		
		this.managers.InitFormManager.subscribe( 'accept', initFormAccept );
		this.managers.InitFormManager.subscribe( 'cancel', _clearInitForm );
	}

	async #from2Dto3D() {

		// clean group scene
		this.#onGroupSelected( null );
		this.managers.SceneManager.sceneGroupUnit.clearSelecteds();

		//TODO: clear zone scene also!!

		//set grid to aquaManager
		this.managers.AquaManager.parseGrid( this.managers.GridManager.grid );
		// draw 3D
		const currentData = this.managers.DataManager.currentData;
		await this.managers.SceneManager.scene3DUnit.initScene( currentData , this.managers.AquaManager.gridAqua );
		
		await this.sceneChanged( SCENES.PERS );		

		this.managers.SceneManager.render();
	}

	async #from3Dto2D() {
		await this.sceneChanged( SCENES.ORTHO );
	}

	async #from3DToGroup() {
		
		// draw 3D
		const currentData = this.managers.DataManager.currentData;
		await this.managers.SceneManager.sceneGroupUnit.initScene( currentData , this.managers.AquaManager.gridAqua );
		
		this.sceneChanged( SCENES.GROUP );

		this.managers.SceneManager.render();
	}

	async #from3DToZone() {
		//set grid to aquaManager
		const currentData = this.managers.DataManager.currentData;
		await this.managers.SceneManager.sceneZoneUnit.initScene( currentData , this.managers.AquaManager.gridAqua );
		
		this.sceneChanged( SCENES.ZONE );

		this.managers.SceneManager.render();
	}

	async #onSelectButtonPushed() {
		const response = await new CellSelection( 'modalContainer' ).show();

		if( !response.ok ) return;

		for( let i=response.start.row; i <= response.end.row; i++ ) {
			for( let j=response.start.column; j <= response.end.column; j++ ) {
				const cell = this.managers.GridManager.grid.getCell( i-1, j-1, true );
				if( cell ) cell.selected = response.select;
			}
		}

		this.#onCellsSelected();

		await this.managers.SceneManager.scene2DUnit.initGrid();
		this.managers.SceneManager.render();
	}

	async #onShowGroup( type, show ) {
		this.managers.SceneManager.sceneGroupUnit.showObject( type, show );
	}

	async #onGroupSelected( data ) {
		this.managers.QuestionFormManager.update( data );
		if( this._showedList ) {
			this._showedList.hide();
			delete this._showedList;
		}

		if( !data ) return;
		
		this._showedList = new List( 'listForm', data.aqua );
		this._showedList.subscribe( 'rowSelected', this.#onListRowSelected.bind( this ) );
		this._showedList.subscribe( 'dataChanged', this.#onListDataChanged.bind( this ) );

		switch( data.type ) {
			case GROUP_TYPE.FRONTALES: {
				this._showedList.show( this.managers.AquaManager.gridAqua.frontales );
			} break;
			case GROUP_TYPE.LATERALES: {
				this._showedList.show( this.managers.AquaManager.gridAqua.laterales );
			} break;
			case GROUP_TYPE.CAPILLAS: {
				this._showedList.show( this.managers.AquaManager.gridAqua.capillas );
			} break;
			case GROUP_TYPE.TRAVERSALES: {
				this._showedList.show( this.managers.AquaManager.gridAqua.transversales );
			} break;
		}
	}

	async #onCreateZones() {
		this.managers.SceneManager.sceneZoneUnit.toggelMode();
		
		const currentMode = this.managers.SceneManager.sceneZoneUnit.mode;
		this.managers.ToolBoxZoneManager.toggleButtonVisibility( 'add', currentMode === EDIT_MODE.EDITING ? true : false );
		this.managers.ToolBoxZoneManager.toggleButtonVisibility( 'remove', currentMode === EDIT_MODE.EDITING ? true : false );
		this.managers.ToolBoxZoneManager.moveSelectedZoneButtons();
	}

	async #onAddZone() {
		await this.managers.SceneManager.sceneZoneUnit.addZone();
	}

	async #onDeleteZone() {
		await this.managers.SceneManager.sceneZoneUnit.deleteZone();
	}

	async #onZoneCellsSelected() {
		this.managers.ToolBoxZoneManager.moveSelectedZoneButtons();
	}

	async #onZoneSelected( data ) {

		if( this._showedList ) {
			this._showedList.hide();
			delete this._showedList;
		}

		if( !data ) return;
		const zone = data.zone;
		if( !zone ) return;
		
		this._showedList = new List( 'listForm', zone );
		this._showedList.subscribe( 'rowSelected', this.#onListRowSelected.bind( this ) );
		this._showedList.subscribe( 'dataChanged', this.#onListDataChanged.bind( this ) );

		this._showedList.show( this.managers.AquaManager.gridAqua.zonas );

		data.type = 'zonas';
		data.aqua = this.managers.AquaManager.gridAqua.zonas;
		console.log( data );
		this.managers.QuestionFormManager.update( data );
		
	}

	async #onListRowSelected( data, type ) {
		const zone = this.managers.AquaManager.gridAqua.zonas.find( z => z === data );
		if( zone ) {
			this.managers.SceneManager.sceneZoneUnit.clearSelection();
			this.managers.SceneManager.sceneZoneUnit.selectCells( data.cells.map( c => c.cell ), true );
		}
		else 
			this.managers.SceneManager.sceneGroupUnit.onObjectSelectedFromOutside( data, type );
	}

	async #onListDataChanged() {
		await this.managers.SceneManager.sceneGroupUnit.initGrid( this.managers.SceneManager.sceneGroupUnit.grid );
	}

	async sceneChanged( scene = SCENES.ORTHO ) {

		this.currentScene = scene;
		this.managers.SceneManager.renderUnit.clearComposer();
		this.managers.SceneManager.renderUnit.doContinuousRender( true );
		
		switch( scene ) {
			case SCENES.ORTHO: {
				await this.managers.ToolBox2DManager.init();
				this.managers.SceneManager.scene2DUnit.clearSelection();
			} break;
			case SCENES.PERS: {				
				await this.managers.ToolBox3DManager.init();
				if( this.__goodLooking ) {				
					const s = this.managers.SceneManager.scene3DUnit.scene;
					const c = this.managers.SceneManager.scene3DUnit.camera;
					this.managers.SceneManager.renderUnit.initComposer( s, c );
					this.managers.SceneManager.renderUnit.doContinuousRender( false );
				}
			} break;
			case SCENES.GROUP: {				
				await this.managers.ToolBoxGroupManager.init();
			} break;
			case SCENES.ZONE: {				
				await this.managers.ToolBoxZoneManager.init();
			} break;
		}
		//show scene
		this.managers.SceneManager.setScene( scene );

		this.managers.SceneManager.render();

	}
}